import React, { useState, useEffect } from 'react';
import { incidentAPI, jobsAPI, teamMembersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import JobSearchSelect from '../components/JobSearchSelect';
import TeamMemberSearchSelect from '../components/TeamMemberSearchSelect';
import {
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Clock,
  MapPin,
  User,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ShieldAlert,
  Eye,
  Edit,
  BarChart3,
  Activity,
  Printer,
} from 'lucide-react';

const IncidentReporting = () => {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    incident_type: 'near_miss',
    severity: 'minor',
    job_id: '',
    location: '',
    witnesses: [],
    immediate_actions: '',
    root_cause: '',
  });

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager' || isAdmin;

  useEffect(() => {
    loadData();
  }, [statusFilter, severityFilter, typeFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (severityFilter) params.severity = severityFilter;
      if (typeFilter) params.incident_type = typeFilter;

      const [incidentsRes, jobsRes, membersRes] = await Promise.all([
        incidentAPI.getAll(params),
        jobsAPI.getAll({}),
        teamMembersAPI.getAll({}),
      ]);
      setIncidents(incidentsRes.data);
      setJobs(jobsRes.data);
      setTeamMembers(membersRes.data);

      if (isAdmin) {
        try {
          const statsRes = await incidentAPI.getStats();
          setStats(statsRes.data);
        } catch (e) {
          console.error('Failed to load stats');
        }
      }
    } catch (error) {
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedIncident) {
        await incidentAPI.update(selectedIncident.id, formData);
        toast.success('Incident updated');
      } else {
        // Include incident_date when creating a new incident
        const submitData = {
          ...formData,
          incident_date: new Date().toISOString(),
        };
        await incidentAPI.create(submitData);
        toast.success('Incident reported');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save incident');
    }
  };

  const handleStatusChange = async (incidentId, newStatus) => {
    try {
      await incidentAPI.update(incidentId, { status: newStatus });
      toast.success(`Incident status updated to ${newStatus}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      incident_type: 'near_miss',
      severity: 'minor',
      job_id: '',
      location: '',
      witnesses: [],
      immediate_actions: '',
      root_cause: '',
    });
    setSelectedIncident(null);
  };

  const openEditModal = (incident) => {
    setSelectedIncident(incident);
    setFormData({
      title: incident.title,
      description: incident.description,
      incident_type: incident.incident_type,
      severity: incident.severity,
      job_id: incident.job_id || '',
      location: incident.location || '',
      witnesses: incident.witnesses || [],
      immediate_actions: incident.immediate_actions || '',
      root_cause: incident.root_cause || '',
    });
    setShowModal(true);
  };

  const openViewModal = (incident) => {
    setSelectedIncident(incident);
    setShowViewModal(true);
  };

  const handlePrintIncident = (incident) => {
    const job = jobs.find(j => j.id === incident.job_id);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Incident Report - ${incident.title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px; }
            .header img { max-width: 300px; height: auto; }
            h1 { color: #1e293b; font-size: 24px; margin: 20px 0 10px; }
            .severity { display: inline-block; padding: 4px 12px; border-radius: 20px; font-weight: bold; margin-left: 10px; }
            .severity-minor { background: #dcfce7; color: #166534; }
            .severity-moderate { background: #fef3c7; color: #92400e; }
            .severity-serious { background: #fed7aa; color: #c2410c; }
            .severity-critical { background: #fecaca; color: #dc2626; }
            .section { margin-bottom: 25px; }
            .section-title { font-size: 16px; font-weight: bold; color: #475569; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-bottom: 12px; }
            .field { margin-bottom: 10px; }
            .label { font-weight: 600; color: #64748b; display: inline-block; width: 150px; }
            .value { color: #1e293b; }
            .description { background: #f8fafc; padding: 15px; border-radius: 8px; margin-top: 10px; white-space: pre-wrap; }
            .footer { margin-top: 40px; text-align: center; color: #64748b; font-size: 12px; border-top: 1px solid #e2e8f0; padding-top: 20px; }
            @media print { body { margin: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png" alt="MBConstruction" />
          </div>
          
          <h1>
            INCIDENT REPORT
            <span class="severity severity-${incident.severity}">${incident.severity?.toUpperCase()}</span>
          </h1>
          
          <div class="section">
            <div class="section-title">Incident Details</div>
            <div class="field"><span class="label">Title:</span> <span class="value">${incident.title}</span></div>
            <div class="field"><span class="label">Type:</span> <span class="value">${typeLabels[incident.incident_type] || incident.incident_type}</span></div>
            <div class="field"><span class="label">Status:</span> <span class="value" style="text-transform: capitalize;">${incident.status}</span></div>
            <div class="field"><span class="label">Date:</span> <span class="value">${incident.incident_date ? format(new Date(incident.incident_date), 'MMMM dd, yyyy HH:mm') : 'N/A'}</span></div>
            <div class="field"><span class="label">Location:</span> <span class="value">${incident.location || 'Not specified'}</span></div>
            ${job ? `<div class="field"><span class="label">Related Job:</span> <span class="value">${job.job_code} - ${job.title}</span></div>` : ''}
          </div>
          
          <div class="section">
            <div class="section-title">Description</div>
            <div class="description">${incident.description || 'No description provided'}</div>
          </div>
          
          ${incident.immediate_actions ? `
          <div class="section">
            <div class="section-title">Immediate Actions Taken</div>
            <div class="description">${incident.immediate_actions}</div>
          </div>
          ` : ''}
          
          ${incident.root_cause ? `
          <div class="section">
            <div class="section-title">Root Cause Analysis</div>
            <div class="description">${incident.root_cause}</div>
          </div>
          ` : ''}
          
          ${incident.corrective_actions ? `
          <div class="section">
            <div class="section-title">Corrective Actions</div>
            <div class="description">${incident.corrective_actions}</div>
          </div>
          ` : ''}
          
          <div class="section">
            <div class="section-title">Report Information</div>
            <div class="field"><span class="label">Reported By:</span> <span class="value">${incident.reported_by_name || incident.reported_by || 'Unknown'}</span></div>
            <div class="field"><span class="label">Report Date:</span> <span class="value">${incident.created_at ? format(new Date(incident.created_at), 'MMMM dd, yyyy HH:mm') : 'N/A'}</span></div>
          </div>
          
          <div class="footer">
            <p>Printed on ${format(new Date(), 'MMMM dd, yyyy HH:mm')}</p>
            <p>MBConstruction (Pty) Ltd - Safety Management System</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const filteredIncidents = incidents.filter((inc) =>
    inc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inc.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const severityColors = {
    minor: 'bg-green-100 text-green-700 border-green-200',
    moderate: 'bg-amber-100 text-amber-700 border-amber-200',
    serious: 'bg-orange-100 text-orange-700 border-orange-200',
    critical: 'bg-red-100 text-red-700 border-red-200',
  };

  const statusColors = {
    reported: 'bg-blue-100 text-blue-700',
    investigating: 'bg-amber-100 text-amber-700',
    resolved: 'bg-green-100 text-green-700',
    closed: 'bg-slate-100 text-slate-700',
  };

  const typeLabels = {
    accident: 'Accident',
    near_miss: 'Near Miss',
    hazard: 'Hazard',
    injury: 'Injury',
    property_damage: 'Property Damage',
  };

  const getJobName = (jobId) => {
    const job = jobs.find((j) => j.id === jobId);
    return job ? `${job.job_code} - ${job.title}` : 'Unknown Job';
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ShieldAlert className="w-6 h-6 text-orange-600" />
            Incident Reporting
          </h1>
          <p className="text-slate-600">Report and track workplace incidents (OHS Compliance)</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Report Incident
        </button>
      </div>

      {/* Stats Cards (Admin Only) */}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Incidents</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Open</p>
                <p className="text-2xl font-bold text-blue-600">{(stats.by_status?.reported || 0) + (stats.by_status?.investigating || 0)}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Critical</p>
                <p className="text-2xl font-bold text-red-600">{stats.by_severity?.critical || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Resolved</p>
                <p className="text-2xl font-bold text-green-600">{stats.by_status?.resolved || 0}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('list')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === 'list' ? 'border-orange-600 text-orange-600' : 'border-transparent text-slate-600'
            }`}
          >
            <FileText className="w-4 h-4" />
            All Incidents ({incidents.length})
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('stats')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'stats' ? 'border-orange-600 text-orange-600' : 'border-transparent text-slate-600'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              Statistics
            </button>
          )}
        </nav>
      </div>

      {activeTab === 'list' && (
        <>
          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search incidents..."
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">All Statuses</option>
                <option value="reported">Reported</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">All Severities</option>
                <option value="minor">Minor</option>
                <option value="moderate">Moderate</option>
                <option value="serious">Serious</option>
                <option value="critical">Critical</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">All Types</option>
                <option value="accident">Accident</option>
                <option value="near_miss">Near Miss</option>
                <option value="hazard">Hazard</option>
                <option value="injury">Injury</option>
                <option value="property_damage">Property Damage</option>
              </select>
            </div>
          </div>

          {/* Incidents List */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                <p className="mt-2 text-slate-500">Loading incidents...</p>
              </div>
            ) : filteredIncidents.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <ShieldAlert className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No incidents found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Incident</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Type</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Severity</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Date</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredIncidents.map((incident) => (
                    <tr key={incident.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900">{incident.title}</p>
                          <p className="text-sm text-slate-500 truncate max-w-xs">{incident.location || 'No location'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">{typeLabels[incident.incident_type]}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${severityColors[incident.severity]}`}>
                          {incident.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isManager ? (
                          <select
                            value={incident.status}
                            onChange={(e) => handleStatusChange(incident.id, e.target.value)}
                            className={`px-2 py-1 rounded text-xs font-medium ${statusColors[incident.status]}`}
                          >
                            <option value="reported">Reported</option>
                            <option value="investigating">Investigating</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        ) : (
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[incident.status]}`}>
                            {incident.status}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-600">
                          {incident.incident_date ? format(new Date(incident.incident_date), 'MMM dd, yyyy') : 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openViewModal(incident)}
                            className="p-1.5 hover:bg-slate-100 rounded"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 text-slate-600" />
                          </button>
                          <button
                            onClick={() => handlePrintIncident(incident)}
                            className="p-1.5 hover:bg-slate-100 rounded"
                            title="Print"
                          >
                            <Printer className="w-4 h-4 text-slate-600" />
                          </button>
                          {isManager && (
                            <button
                              onClick={() => openEditModal(incident)}
                              className="p-1.5 hover:bg-slate-100 rounded"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4 text-slate-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Statistics Tab */}
      {activeTab === 'stats' && isAdmin && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* By Status */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">By Status</h3>
              <div className="space-y-3">
                {Object.entries(stats.by_status || {}).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${
                        status === 'reported' ? 'bg-blue-500' :
                        status === 'investigating' ? 'bg-amber-500' :
                        status === 'resolved' ? 'bg-green-500' : 'bg-slate-400'
                      }`}></span>
                      <span className="text-sm text-slate-700 capitalize">{status}</span>
                    </div>
                    <span className="font-medium text-slate-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Severity */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-semibold text-slate-900 mb-4">By Severity</h3>
              <div className="space-y-3">
                {Object.entries(stats.by_severity || {}).map(([severity, count]) => (
                  <div key={severity} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${
                        severity === 'minor' ? 'bg-green-500' :
                        severity === 'moderate' ? 'bg-amber-500' :
                        severity === 'serious' ? 'bg-orange-500' : 'bg-red-500'
                      }`}></span>
                      <span className="text-sm text-slate-700 capitalize">{severity}</span>
                    </div>
                    <span className="font-medium text-slate-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By Type */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 md:col-span-2">
              <h3 className="font-semibold text-slate-900 mb-4">By Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(stats.by_type || {}).map(([type, count]) => (
                  <div key={type} className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-sm text-slate-600">{typeLabels[type]}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedIncident ? 'Edit Incident' : 'Report New Incident'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Brief description of the incident"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                  <select
                    value={formData.incident_type}
                    onChange={(e) => setFormData({ ...formData, incident_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    required
                  >
                    <option value="near_miss">Near Miss</option>
                    <option value="accident">Accident</option>
                    <option value="hazard">Hazard</option>
                    <option value="injury">Injury</option>
                    <option value="property_damage">Property Damage</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Severity *</label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    required
                  >
                    <option value="minor">Minor</option>
                    <option value="moderate">Moderate</option>
                    <option value="serious">Serious</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Related Job</label>
                <JobSearchSelect
                  jobs={jobs}
                  value={formData.job_id}
                  onChange={(jobId) => setFormData({ ...formData, job_id: jobId })}
                  placeholder="Search and select a job..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Where did the incident occur?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="Detailed description of what happened..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Immediate Actions Taken</label>
                <textarea
                  value={formData.immediate_actions}
                  onChange={(e) => setFormData({ ...formData, immediate_actions: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  placeholder="What actions were taken immediately after the incident?"
                />
              </div>

              {isManager && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Root Cause Analysis</label>
                  <textarea
                    value={formData.root_cause}
                    onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                    placeholder="What was the root cause of this incident?"
                  />
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg"
                >
                  {selectedIncident ? 'Update Incident' : 'Report Incident'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedIncident && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Incident Details</h3>
              <button
                onClick={() => { setShowViewModal(false); setSelectedIncident(null); }}
                className="p-2 hover:bg-slate-100 rounded"
              >
                <XCircle className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h4 className="text-xl font-semibold text-slate-900">{selectedIncident.title}</h4>
                <div className="flex items-center gap-3 mt-2">
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${severityColors[selectedIncident.severity]}`}>
                    {selectedIncident.severity}
                  </span>
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedIncident.status]}`}>
                    {selectedIncident.status}
                  </span>
                  <span className="text-sm text-slate-500">{typeLabels[selectedIncident.incident_type]}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Date</p>
                  <p className="font-medium text-slate-900">
                    {selectedIncident.incident_date ? format(new Date(selectedIncident.incident_date), 'MMM dd, yyyy HH:mm') : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-medium text-slate-900">{selectedIncident.location || 'Not specified'}</p>
                </div>
              </div>

              {selectedIncident.job_id && (
                <div>
                  <p className="text-sm text-slate-500">Related Job</p>
                  <p className="font-medium text-slate-900">{getJobName(selectedIncident.job_id)}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-slate-500 mb-1">Description</p>
                <p className="text-slate-700 whitespace-pre-wrap">{selectedIncident.description}</p>
              </div>

              {selectedIncident.immediate_actions && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Immediate Actions Taken</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedIncident.immediate_actions}</p>
                </div>
              )}

              {selectedIncident.root_cause && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Root Cause Analysis</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedIncident.root_cause}</p>
                </div>
              )}

              {selectedIncident.corrective_actions && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Corrective Actions</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedIncident.corrective_actions}</p>
                </div>
              )}

              <div className="pt-4 border-t border-slate-200 text-sm text-slate-500">
                <p>Reported by: {selectedIncident.reported_by_name || selectedIncident.reported_by || 'Unknown'}</p>
                <p>Created: {selectedIncident.created_at ? format(new Date(selectedIncident.created_at), 'MMM dd, yyyy HH:mm') : 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentReporting;
